import {
  useMemo,
  FunctionComponent,
  forwardRef,
  PropsWithoutRef,
  Ref,
  useReducer,
  ChangeEvent,
  useEffect
} from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp } from '../../../theme';
import { ForwardProps } from '../../../types';
import { range } from '../../../utils';
import {
  useConfiguration,
  useConsolidatedRef,
  useDirection,
  useFocusWithin,
  useI18n
} from '../../../hooks';
import FormField from '../../FormField';
import Select, { Option } from '../../Select';
import { StyledSelect } from '../../Select/Select';
import Flex from '../../Flex';
import { StyledFormControl } from '../../FormControl';
import DateTimeProps, { DateTimeCallbackParameter } from '../DateTime.types';

import { parseToDate, getLocalizedMonths } from './utils';
import StyledDateTime from './DateTime.styles';

export interface MonthInputProps extends DateTimeProps {}

export const StyledMonthInput = styled(StyledDateTime)<MonthInputProps>(() => {
  const { start, end } = useDirection();
  return css`
    padding: 0;
    border: 0;
    ${StyledSelect} {
      min-width: 0;
      &:first-child {
        border-top-${end}-radius: 0;
        border-bottom-${end}-radius: 0;
      }
      &:last-child {
        max-width: max-content;
        margin-inline-start: -0.0625rem;
        border-top-${start}-radius: 0;
        border-bottom-${start}-radius: 0;
      }
    }
  `;
});

StyledMonthInput.defaultProps = defaultThemeProp;

const convertToCallbackParameter = (
  date: Date,
  parts: (string | undefined)[]
): DateTimeCallbackParameter => {
  if (parts.every(Boolean)) {
    const [year, month] = parts.map(Number);
    date.setUTCFullYear(year, month);
    return {
      valueAsISOString: date.toISOString(),
      valueAsTimestamp: date.getTime()
    };
  }
  if (parts.every(p => !p)) {
    return {
      valueAsISOString: '',
      valueAsTimestamp: undefined
    };
  }
  return {
    valueAsISOString: '',
    valueAsTimestamp: NaN,
    state: 'incomplete'
  };
};

type Field = 'month' | 'year';
type State = [year: string | undefined, month: string | undefined];

const reducer = (state: State, action: { field: Field; value: string | undefined }): State => {
  switch (action.field) {
    case 'year':
      return [action.value, state[1]];
    case 'month':
      return [state[0], action.value];
    default:
      return state.slice(0) as State;
  }
};

const MonthInput: FunctionComponent<MonthInputProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<MonthInputProps>, ref: Ref<HTMLFieldSetElement>) => {
    const { locale } = useConfiguration();
    const {
      defaultValue,
      value = defaultValue,
      min,
      max,
      id,
      label,
      labelHidden,
      info,
      status,
      required,
      readOnly,
      disabled,
      onChange,
      onFocus,
      onBlur,
      autoFocus,
      ...restProps
    } = props;

    const date = value ? parseToDate(value) : undefined;
    const currentYear = date ? date.getUTCFullYear() : new Date().getFullYear();

    const minYear = min ? parseToDate(min).getUTCFullYear() : currentYear - 10;
    const maxYear = max ? parseToDate(max).getUTCFullYear() : currentYear + 10;

    const years = range(minYear, maxYear);
    const months = useMemo(() => getLocalizedMonths(locale), [locale]);

    const [state, dispatch] = useReducer(reducer, [
      date?.getUTCFullYear().toString(),
      date?.getUTCMonth().toString()
    ]);

    useEffect(() => {
      dispatch({ field: 'month', value: date?.getUTCMonth().toString() });
      dispatch({ field: 'year', value: date?.getUTCFullYear().toString() });
    }, [date?.toLocaleDateString()]);

    const onFocusChange = (focused: boolean) => {
      const callbackParam = convertToCallbackParameter(date ? new Date(date) : new Date(), state);
      if (onFocus && focused) onFocus(callbackParam);
      else if (onBlur && !focused) onBlur(callbackParam);
    };

    const onSelectChange = (field: Field) => (ev: ChangeEvent<HTMLSelectElement>) => {
      const action = { field, value: ev.target.value };
      dispatch(action);
      onChange?.(
        convertToCallbackParameter(date ? new Date(date) : new Date(), reducer(state, action))
      );
    };

    const containerRef = useConsolidatedRef<HTMLElement>(ref);
    useFocusWithin([containerRef], onFocusChange);

    const t = useI18n();

    const monthOptions = months.map((month, index) => (
      <Option key={month} value={index.toString()}>
        {months[index]}
      </Option>
    ));
    const yearOptions = years.map(year => (
      <Option key={year.toString()} value={year.toString()}>
        {year.toString()}
      </Option>
    ));

    const Month = (
      <Select
        key='month'
        aria-label={t('month_label_a11y')}
        readOnly={readOnly}
        required={required}
        value={state[1]?.toString() ?? ''}
        onChange={onSelectChange('month')}
        status={status}
        disabled={disabled}
        autoFocus={autoFocus}
      >
        {!required && <Option key='null'> </Option>}
        {monthOptions}
      </Select>
    );

    const Year = (
      <Select
        aria-label={t('year_label_a11y')}
        key='year'
        readOnly={readOnly}
        required={required}
        value={state[0]?.toString() ?? ''}
        onChange={onSelectChange('year')}
        status={status}
        disabled={disabled}
      >
        {!required && <Option key='null'> </Option>}
        {yearOptions}
      </Select>
    );

    const Comp = (
      <Flex
        as={StyledMonthInput}
        forwardedAs={StyledFormControl}
        container={{ alignItems: 'center', wrap: 'nowrap' }}
        ref={containerRef}
        status={status}
        disabled={disabled}
        readOnly={readOnly}
        {...restProps}
      >
        {Month}
        {Year}
      </Flex>
    );

    return label ? (
      <FormField
        as='fieldset'
        labelAs='legend'
        {...{ label, labelHidden, id, info, status, required, disabled }}
      >
        {Comp}
      </FormField>
    ) : (
      Comp
    );
  }
);

export default MonthInput;
